/** Parse a YouTube URL or `youtube:VIDEO_ID` value into a video id, or null. */
export function parseYouTubeVideoId(input) {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("youtube:")) {
    const id = trimmed.slice("youtube:".length).trim();
    return id || null;
  }

  try {
    const url = trimmed.startsWith("http") ? new URL(trimmed) : null;
    if (!url) return null;

    const host = url.hostname.replace(/^www\./, "").replace(/^m\./, "m.");

    if (host === "youtu.be") {
      const id = url.pathname.slice(1).split("/")[0];
      return id || null;
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      if (url.pathname.startsWith("/shorts/")) {
        return url.pathname.split("/")[2] || null;
      }
      if (url.pathname.startsWith("/embed/")) {
        return url.pathname.split("/")[2] || null;
      }
      return url.searchParams.get("v") || null;
    }
  } catch {
    return null;
  }

  return null;
}

export function isYouTubeVideo(value) {
  return parseYouTubeVideoId(value) !== null;
}

/** Normalize user input to the stored `youtube:VIDEO_ID` format. */
export function normalizeYouTubeVideo(value) {
  const id = parseYouTubeVideoId(value);
  return id ? `youtube:${id}` : null;
}

export function getYouTubeEmbedUrl(value) {
  const id = parseYouTubeVideoId(value);
  return id
    ? `https://www.youtube.com/embed/${id}?playsinline=1&rel=0`
    : null;
}

export function getYouTubeWatchUrl(value) {
  const id = parseYouTubeVideoId(value);
  return id ? `https://www.youtube.com/watch?v=${id}` : null;
}

export function getYouTubeThumbnailUrl(value) {
  const id = parseYouTubeVideoId(value);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

const youtubeTitleCache = new Map();
const youtubeTitleInflight = new Map();

/** Fetch a YouTube video title via the public oEmbed endpoint. Results are cached by video id. */
export async function fetchYouTubeVideoTitle(value) {
  const id = parseYouTubeVideoId(value);
  if (!id) return null;

  if (youtubeTitleCache.has(id)) return youtubeTitleCache.get(id);

  if (youtubeTitleInflight.has(id)) return youtubeTitleInflight.get(id);

  const watchUrl = getYouTubeWatchUrl(value);
  const promise = fetch(
    `https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl)}&format=json`
  )
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      const title = data?.title?.trim() || null;
      if (title) youtubeTitleCache.set(id, title);
      youtubeTitleInflight.delete(id);
      return title;
    })
    .catch(() => {
      youtubeTitleInflight.delete(id);
      return null;
    });

  youtubeTitleInflight.set(id, promise);
  return promise;
}

export function getYouTubeDisplayLabel(value, title) {
  const id = parseYouTubeVideoId(value);
  if (!id) return "";
  const resolvedTitle = title?.trim();
  if (resolvedTitle) return `${resolvedTitle} - youtube`;
  return "YouTube video";
}
